import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { PageHeader, Card, Loading, CompStatus } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { DateInput } from "../../components/DateInput";
import { Label } from "../../components/ui/label";
import { MoneyInput } from "../../components/MoneyInput";
import { Progress } from "../../components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

export function CompStatusSelect({ comp, companyId, onChanged }) {
  const { meta } = useAuth();
  const change = async (status) => {
    try { await api.patch(`/competencias/${comp.ref}/status`, { status }, { params: { company_id: companyId } }); toast.success(status === "pronta" ? "Competência publicada — snapshot gerencial gerado" : "Status atualizado"); onChanged && onChanged(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Select value={comp.status} onValueChange={change}>
      <SelectTrigger data-testid={`comp-status-select-${companyId}`} className="w-[230px] bg-white h-9"><SelectValue /></SelectTrigger>
      <SelectContent>{meta.competencia_statuses.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

export function PublishDialog({ open, onOpenChange, companyId, comp, onDone }) {
  const [f, setF] = useState({ category: "folha", value: "", employees: "", due_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!file) { toast.error("Selecione o arquivo."); return; }
    const fd = new FormData();
    fd.append("file", file); fd.append("category", f.category); fd.append("company_id", companyId);
    if (f.category === "arquivo_bancario") ["value", "employees", "due_date"].forEach((k) => f[k] && fd.append(k, f[k]));
    setBusy(true);
    try { await api.post(`/competencias/${comp.ref}/publish`, fd); toast.success("Publicado para o cliente"); onOpenChange(false); onDone && onDone(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="publish-dialog"><DialogHeader><DialogTitle>Publicar retorno da folha — {comp?.label}</DialogTitle><DialogDescription>Arquivos gerados no sistema de folha (resumo, encargos, arquivo bancário). Importação automática: integração pendente.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Tipo</Label><Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="publish-category" className="mt-1.5"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="folha">Resumo / relatórios da folha</SelectItem><SelectItem value="encargos">Encargos e guias</SelectItem><SelectItem value="arquivo_bancario">Arquivo bancário</SelectItem><SelectItem value="outros">Outros</SelectItem></SelectContent></Select></div>
          <Input data-testid="publish-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          {f.category === "arquivo_bancario" && <div className="grid grid-cols-3 gap-2">
            <div><Label>Valor (R$)</Label><MoneyInput data-testid="publish-value" className="mt-1" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} /></div>
            <div><Label>Funcionários</Label><Input data-testid="publish-employees" type="number" className="mt-1" value={f.employees} onChange={(e) => setF({ ...f, employees: e.target.value })} /></div>
            <div><Label>Data prevista</Label><DateInput testid="publish-due" className="mt-1" value={f.due_date} onChange={(v) => setF({ ...f, due_date: v })} /></div></div>}
        </div>
        <DialogFooter><Button data-testid="publish-submit" onClick={send} disabled={busy}>{busy ? "Publicando..." : "Publicar"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function BoCompetencias() {
  const [rows, setRows] = useState(null);
  const [pub, setPub] = useState(null);
  const load = () => api.get("/backoffice/competencias").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  if (!rows) return <Loading />;
  return (
    <div>
      <PageHeader eyebrow="Backoffice DP" title={`Competências — ${rows[0]?.label || ""}`} subtitle="Fluxo: Aberta → Aguardando processamento → Em processamento → Em conferência → Pronta para cliente. O processamento técnico ocorre no sistema de folha, fora da visão do cliente." />
      <Card className="overflow-x-auto"><table className="w-full text-sm" data-testid="bo-competencias-table">
        <thead className="bg-slate-50 text-xs text-slate-500"><tr>{["Empresa", "Checklist do cliente", "Confirmado por", "Status", "Ações"].map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h}</th>)}</tr></thead>
        <tbody>{rows.map((c) => (
          <tr key={c.id} className="border-t"><td className="px-4 py-3 font-medium">{c.company}</td>
            <td className="px-4"><div className="flex items-center gap-2 w-48"><Progress value={c.progress} className="h-1.5" /><span className="font-mono text-xs">{c.progress}%</span></div></td>
            <td className="px-4 text-xs text-slate-600">{c.confirmed_by || "—"}</td><td className="px-4"><CompStatus s={c.status} label={c.status_label} /></td>
            <td className="px-4 py-2"><div className="flex gap-2"><CompStatusSelect comp={c} companyId={c.company_id} onChanged={load} /><Button size="sm" variant="outline" data-testid={`publish-button-${c.company_id}`} onClick={() => setPub(c)}><Upload className="h-3.5 w-3.5 mr-1" />Publicar</Button></div></td></tr>))}</tbody>
      </table></Card>
      <PublishDialog open={!!pub} onOpenChange={(o) => !o && setPub(null)} companyId={pub?.company_id} comp={pub} onDone={load} />
    </div>
  );
}
